import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import LearnerHint from "@/components/LearnerHint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Clock, LogIn, LogOut, MapPin, Loader2, AlertTriangle, Check, X,
  History, MessageSquare, Send, Edit, RefreshCw, Calendar,
} from "lucide-react";
import { toast } from "sonner";

const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDate = (iso) => iso ? iso.slice(0, 10) : "—";
const fmtDuration = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
};

export default function Fichaje() {
  const { user } = useAuth();
  const [estado, setEstado] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [correcciones, setCorrecciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ficheando, setFicheando] = useState(false);
  const [showCorr, setShowCorr] = useState(null); // fichaje seleccionado
  const [nuevaCorr, setNuevaCorr] = useState({ motivo: "", tipo: "entrada", timestamp: "" });
  const [showTrabajo, setShowTrabajo] = useState(false); // dialog antes de fichar salida
  const [trabajoRealizado, setTrabajoRealizado] = useState("");
  const [tick, setTick] = useState(0); // forzar refresh del contador

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, h, c] = await Promise.all([
        api.get("/fichajes/mi-estado"),
        api.get("/fichajes/mis"),
        api.get("/fichajes/correcciones"),
      ]);
      setEstado(e.data);
      setHistorial(h.data || []);
      setCorrecciones(c.data || []);
    } catch (err) {
      toast.error("Error cargando fichaje");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Tick para actualizar contador de minutos cada 30s
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const getGeo = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Tu navegador no soporta geolocalización"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      (err) => reject(new Error(err.message || "No se pudo obtener la ubicación")),
      { enableHighAccuracy: true, timeout: 12000 }
    );
  });

  const fichar = async (tipo, trabajoOpcional = null) => {
    setFicheando(true);
    try {
      let geo;
      try {
        geo = await getGeo();
      } catch (e) {
        toast.error(`GPS obligatorio: ${e.message}`);
        setFicheando(false);
        return;
      }
      await api.post("/fichajes", {
        tipo, geo,
        trabajo_realizado: trabajoOpcional || null,
      });
      toast.success(tipo === "entrada" ? "✅ Entrada registrada" : "✅ Salida registrada");
      setShowTrabajo(false);
      setTrabajoRealizado("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al fichar");
    } finally {
      setFicheando(false);
    }
  };

  const enviarCorreccion = async () => {
    if (!nuevaCorr.motivo.trim()) return toast.error("El motivo es obligatorio");
    try {
      const payload = {
        motivo: nuevaCorr.motivo,
        nuevo_fichaje: !showCorr,  // si no se selecciona un fichaje existente
        tipo_propuesto: nuevaCorr.tipo,
        timestamp_propuesto: nuevaCorr.timestamp ? new Date(nuevaCorr.timestamp).toISOString() : null,
      };
      await api.post("/fichajes/correcciones", payload);
      toast.success("Solicitud enviada. El admin la revisará.");
      setShowCorr(null);
      setNuevaCorr({ motivo: "", tipo: "entrada", timestamp: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  // Agrupar historial por fecha
  const porFecha = {};
  historial.forEach((f) => {
    porFecha[f.fecha] = porFecha[f.fecha] || [];
    porFecha[f.fecha].push(f);
  });
  const fechasOrdenadas = Object.keys(porFecha).sort().reverse();

  // Minutos en curso calculados en cliente (se actualizan con `tick`, sin perder la jornada al cambiar de día)
  const minutosEnCurso = (estado?.abierto && estado.entrada_abierta_desde)
    ? Math.max(0, Math.floor((Date.now() - new Date(estado.entrada_abierta_desde)) / 60000))
    : 0;
  const minutosTotales = estado ? (estado.minutos_trabajados - (estado.minutos_en_curso || 0)) + minutosEnCurso : 0;

  if (estado && !estado.puede_fichar && !estado.abierto) {
    const motivo = estado.motivo_no_puede;
    if (motivo === "sin_ruta_hoy") {
      return (
        <div className="p-6">
          <PageHeader title="Fichaje horario" />
          <div className="bg-zinc-900/40 border border-amber-500/40 rounded-xl p-8 text-center max-w-md mx-auto"
            data-testid="fichaje-sin-ruta">
            <Calendar className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-2">Sin ruta asignada hoy</h2>
            <p className="text-sm text-zinc-400 mb-3">
              Como <b>montador externo</b> solo puedes fichar en días en los que tienes una ruta asignada por DNF.
            </p>
            <p className="text-xs text-zinc-500">
              Hoy {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })} no tienes ruta planificada.
              <br />Si crees que es un error, contacta con producción.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="p-6">
        <PageHeader title="Fichaje horario" />
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-8 text-center max-w-md mx-auto">
          <Check className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-2">No estás obligado a fichar</h2>
          <p className="text-sm text-zinc-400">
            Tu perfil está marcado como <b>autónomo</b> o <b>exento</b>. No necesitas registro horario.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="fichaje-page">
      <PageHeader title="Fichaje horario"
        subtitle="Registro de jornada según Ley 10/2021 · geolocalización obligatoria" />

      {/* Acción principal */}
      {estado && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-6 mb-6">
          <div className="flex flex-col items-center text-center">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Hoy</div>
            <div className="text-2xl font-bold text-zinc-100 mb-1">
              {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <div className="text-sm text-zinc-400 mb-2">
              <span suppressHydrationWarning data-testid="fichaje-minutos-trabajados">{tick !== null && fmtDuration(minutosTotales)}</span> trabajadas
              {estado.abierto && <span className="ml-2 inline-flex items-center gap-1 text-emerald-300">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span> En curso
              </span>}
            </div>

            {estado.abierto && estado.jornada_abierta_label && (
              <div className={`mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border ${
                estado.jornada_desde_ayer
                  ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-200"
                  : "bg-zinc-800/60 border-zinc-700 text-zinc-300"}`}
                data-testid="jornada-abierta-label">
                <Clock className="w-3.5 h-3.5" />
                <span>{estado.jornada_abierta_label}{estado.jornada_desde_ayer && " · cruza medianoche"}</span>
              </div>
            )}

            {estado.entrada_huerfana && (
              <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs text-red-200 max-w-md"
                data-testid="entrada-huerfana-aviso">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Tienes una entrada del <b>{estado.entrada_abierta_fecha}</b> abierta más de {estado.max_jornada_h} h.
                No se puede cerrar automáticamente: solicita una corrección al admin.
              </div>
            )}

            {estado.ruta_hoy && (
              <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs"
                data-testid="ruta-hoy-badge">
                <Calendar className="w-3.5 h-3.5" />
                <span>Ruta de hoy: <b>{estado.ruta_hoy.nombre || estado.ruta_hoy.codigo || "—"}</b></span>
              </div>
            )}

            {estado.siguiente_accion === null ? null : estado.siguiente_accion === "entrada" ? (
              <LearnerHint label="Fichar entrada al empezar el día (geolocalizado)">
                <Button onClick={() => fichar("entrada")} disabled={ficheando}
                  data-testid="btn-fichar-entrada"
                  className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold text-lg h-16 px-12 rounded-2xl shadow-lg">
                  {ficheando ? <Loader2 className="w-6 h-6 mr-3 animate-spin" /> : <LogIn className="w-6 h-6 mr-3" />}
                  Fichar entrada
                </Button>
              </LearnerHint>
            ) : (
              <LearnerHint label="Fichar salida al terminar el día">
                <Button onClick={() => setShowTrabajo(true)} disabled={ficheando}
                  data-testid="btn-fichar-salida"
                  className="bg-red-500 hover:bg-red-400 text-white font-bold text-lg h-16 px-12 rounded-2xl shadow-lg">
                  {ficheando ? <Loader2 className="w-6 h-6 mr-3 animate-spin" /> : <LogOut className="w-6 h-6 mr-3" />}
                  Fichar salida
                </Button>
              </LearnerHint>
            )}

            <div className="mt-3 flex items-center gap-1 text-[10px] text-zinc-500">
              <MapPin className="w-3 h-3" /> Se solicitará tu ubicación
            </div>
          </div>

          {/* Marcas de hoy */}
          {estado.fichajes_hoy.length > 0 && (
            <div className="mt-5 pt-5 border-t border-zinc-800 grid grid-cols-2 gap-2">
              {estado.fichajes_hoy.map((f) => (
                <div key={f.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-xs"
                  data-testid={`fichaje-hoy-${f.id}`}>
                  <div className="flex items-center justify-between">
                    <span className={`flex items-center gap-1 font-medium ${f.tipo === "entrada" ? "text-emerald-300" : "text-red-300"}`}>
                      {f.tipo === "entrada" ? <LogIn className="w-3 h-3" /> : <LogOut className="w-3 h-3" />}
                      {f.tipo === "entrada" ? "Entrada" : "Salida"}
                    </span>
                    <span className="text-zinc-300 font-mono">{fmtTime(f.timestamp)}</span>
                  </div>
                  {f.edited_at && (
                    <div className="text-[9px] text-amber-400 mt-1 flex items-center gap-1">
                      <Edit className="w-2.5 h-2.5" /> Editado por admin
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Botón solicitar corrección si falta algo */}
      <div className="flex justify-end mb-4">
        <Button variant="outline" size="sm" onClick={() => { setShowCorr(true); setNuevaCorr({ motivo: "", tipo: "entrada", timestamp: "" }); }}
          className="bg-transparent border-zinc-700 text-zinc-300" data-testid="btn-corregir">
          <MessageSquare className="w-3.5 h-3.5 mr-2" /> Solicitar corrección
        </Button>
      </div>

      {/* Correcciones pendientes propias */}
      {correcciones.filter((c) => c.estado === "pendiente").length > 0 && (
        <div className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3"
          data-testid="correcciones-pendientes">
          <div className="flex items-center gap-2 text-amber-300 text-sm font-medium mb-2">
            <RefreshCw className="w-3.5 h-3.5" /> Tus correcciones pendientes
          </div>
          {correcciones.filter((c) => c.estado === "pendiente").map((c) => (
            <div key={c.id} className="text-xs text-zinc-300 py-1">
              📝 {c.motivo} {c.timestamp_propuesto && `(${fmtDate(c.timestamp_propuesto)} ${fmtTime(c.timestamp_propuesto)})`}
            </div>
          ))}
        </div>
      )}

      {/* Historial */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
        <h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
          <History className="w-3.5 h-3.5" /> Mi historial
        </h2>
        {loading ? (
          <div className="text-center py-6 text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin" /> Cargando…</div>
        ) : fechasOrdenadas.length === 0 ? (
          <div className="text-center text-sm text-zinc-500 py-6">Sin fichajes aún</div>
        ) : (
          <div className="space-y-3" data-testid="fichaje-historial">
            {fechasOrdenadas.slice(0, 14).map((fecha) => {
              const lista = porFecha[fecha];
              // calcular horas del día
              let min = 0;
              let pe = null;
              lista.slice().sort((a, b) => a.timestamp.localeCompare(b.timestamp)).forEach((f) => {
                if (f.tipo === "entrada") pe = f.timestamp;
                else if (f.tipo === "salida" && pe) {
                  min += Math.round((new Date(f.timestamp) - new Date(pe)) / 60000);
                  pe = null;
                }
              });
              return (
                <div key={fecha} className="bg-zinc-950 border border-zinc-800 rounded p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-zinc-200">
                      {new Date(fecha).toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" })}
                    </div>
                    <div className="text-xs text-zinc-400">{min > 0 ? fmtDuration(min) : "—"}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {lista.slice().sort((a, b) => a.timestamp.localeCompare(b.timestamp)).map((f) => (
                      <span key={f.id}
                        className={`text-[10px] px-2 py-0.5 rounded ${f.tipo === "entrada" ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/10 text-red-300"}`}
                        data-testid={`fichaje-${f.id}`}>
                        {f.tipo === "entrada" ? "↑" : "↓"} {fmtTime(f.timestamp)}
                        {f.edited_at && " *"}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialog trabajo realizado antes de salir */}
      <Dialog open={showTrabajo} onOpenChange={setShowTrabajo}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md"
          data-testid="dialog-trabajo">
          <DialogHeader>
            <DialogTitle>Fichar salida</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Si quieres, describe brevemente el trabajo realizado hoy (opcional).
            </DialogDescription>
          </DialogHeader>
          <Textarea value={trabajoRealizado} onChange={(e) => setTrabajoRealizado(e.target.value)}
            placeholder="Ej. Instalación 3 vinilos en CC Sevilla, recogida material almacén central…"
            className="bg-zinc-900 border-zinc-800 min-h-[80px]"
            data-testid="trabajo-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrabajo(false)}
              className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
            <Button onClick={() => fichar("salida", trabajoRealizado)} disabled={ficheando}
              className="bg-red-500 hover:bg-red-400 text-white" data-testid="confirm-salida">
              <LogOut className="w-4 h-4 mr-2" /> Fichar salida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog solicitar corrección */}
      <Dialog open={!!showCorr} onOpenChange={(o) => !o && setShowCorr(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md"
          data-testid="dialog-correccion">
          <DialogHeader>
            <DialogTitle>Solicitar corrección</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Si olvidaste fichar o cometiste un error. El admin revisará tu solicitud.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-400">Motivo *</Label>
              <Textarea value={nuevaCorr.motivo}
                onChange={(e) => setNuevaCorr({ ...nuevaCorr, motivo: e.target.value })}
                placeholder="Ej. Olvidé fichar la entrada al llegar al cliente"
                className="bg-zinc-900 border-zinc-800 min-h-[60px]"
                data-testid="corr-motivo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Tipo</Label>
                <Select value={nuevaCorr.tipo} onValueChange={(v) => setNuevaCorr({ ...nuevaCorr, tipo: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="corr-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="entrada">Entrada</SelectItem>
                    <SelectItem value="salida">Salida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Fecha y hora propuestas</Label>
                <Input type="datetime-local" value={nuevaCorr.timestamp}
                  onChange={(e) => setNuevaCorr({ ...nuevaCorr, timestamp: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="corr-timestamp" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorr(null)}
              className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
            <Button onClick={enviarCorreccion}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="corr-enviar">
              <Send className="w-3.5 h-3.5 mr-2" /> Enviar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
